import { humanizeSnakeCase } from '@/lib/humanize';

import { RowSchemaColumn } from './types';

/**
 * Column descriptors for the editable row-truth fields. Keys are RAW snake_case
 * (the canonical payload shape — see the case-conversion caution in `./api.ts`).
 */
export type ColumnKind =
    | 'text'
    | 'number'
    | 'bool'
    | 'list'
    | 'date'
    | 'integer'
    | 'decimal'
    | 'enum'
    | 'long_text';

export interface ColumnDef {
    key: string;
    label: string;
    kind: ColumnKind;
    /** Help text, shown as the column header tooltip. */
    description?: string;
    /** True for a reviewer-defined field; the attributes below are only set then. */
    custom?: boolean;
    /** Reviewers are prompted to fill it; saving never requires it. */
    required?: boolean;
    choices?: string[];
    ge?: number;
    le?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    defaultValue?: string | number | boolean;
    /** Shown but not editable: a value from an earlier field-group version. */
    readOnly?: boolean;
}

/** Every `ColumnKind` the grid knows how to render — see {@link toColumnDef}. */
const VALID_KINDS: ReadonlySet<string> = new Set([
    'text',
    'number',
    'bool',
    'list',
    'date',
    'integer',
    'decimal',
    'enum',
    'long_text',
]);

/** The optional descriptor attributes copied onto a `ColumnDef` only when the backend sent a value. */
const OPTIONAL_ATTRIBUTES = [
    'description',
    'choices',
    'ge',
    'le',
    'minLength',
    'maxLength',
    'pattern',
] as const;

/**
 * Translate the schema endpoint's column descriptors into grid `ColumnDef`s —
 * this is what makes the grid render off the doc's actual row model instead
 * of a hardcoded `LineItem` shape. An unrecognized `kind` (e.g. a future
 * backend addition the frontend hasn't learned yet) falls back to `'text'`
 * rather than crashing the grid. The spec's `boolean` is the grid's `bool`.
 * Optional attributes are copied only when set, so a built-in column's
 * `ColumnDef` is exactly what it was before custom fields.
 */
export function toColumnDef(column: RowSchemaColumn): ColumnDef {
    const kind = column.kind === 'boolean' ? 'bool' : column.kind;
    const def: ColumnDef = {
        key: column.key,
        label: column.label,
        kind: VALID_KINDS.has(kind) ? (kind as ColumnKind) : 'text',
    };
    for (const name of OPTIONAL_ATTRIBUTES) {
        const value = column[name];
        if (value != null) Object.assign(def, { [name]: value });
    }
    if (column.custom) {
        def.custom = true;
        def.required = column.required;
        if (column.default != null) def.defaultValue = column.default;
    }
    return def;
}

/** The blank value `makeBlankDraftRow` seeds for a column of a given kind. */
export function blankValueForKind(kind: ColumnKind): unknown {
    switch (kind) {
        case 'bool':
            return false;
        case 'number':
        case 'integer':
        case 'decimal':
        case 'enum':
            return null;
        case 'list':
            return [];
        case 'text':
        case 'long_text':
        case 'date':
        default:
            return '';
    }
}

/**
 * The blank value for one column: a custom column starts from its default, and
 * every unset value is `null` (a boolean too, so "not answered" differs from "no").
 */
function blankValueFor(column: ColumnDef): unknown {
    if (!column.custom) return blankValueForKind(column.kind);
    return column.defaultValue ?? null;
}

/** A blank row payload seeding every column in `columns` with its empty value. */
export function blankPayloadFor(columns: ColumnDef[]): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    for (const c of columns) {
        payload[c.key] = blankValueFor(c);
    }
    return payload;
}

/** A copy of `payload` with each custom default filled in for a key never stored; a stored null stays null. */
export function seedCustomDefaults(
    payload: Record<string, unknown>,
    columns: readonly ColumnDef[],
): Record<string, unknown> {
    const seeded = { ...payload };
    for (const c of columns) {
        if (c.custom && c.defaultValue != null && !(c.key in seeded)) {
            seeded[c.key] = c.defaultValue;
        }
    }
    return seeded;
}

/**
 * Payload keys that are deliberately NOT surfaced as editable columns even when
 * present in the data: the free-form nested dicts (`meta`, `field_verifications`)
 * a flat cell can't render, and the computed `mro_refs` the server strips. They
 * still round-trip untouched on save — they're just not shown in the grid.
 */
export const NON_COLUMN_KEYS = new Set([
    'meta',
    'field_verifications',
    'mro_refs',
]);

/**
 * Turn a raw snake_case key into a human label for an unknown (extra) column,
 * e.g. `inspection_zone` → "Inspection Zone".
 * Re-exported from `@/lib/humanize` for callers that already import from here.
 */
export { humanizeSnakeCase as humanizeKey };

/**
 * Whether a value is safe to render/edit as a plain `'text'` column. Extra
 * columns are always rendered as text (see {@link deriveColumns}), and
 * `Cell`'s text branch does `String(value)` — a non-primitive (object, or an
 * array containing a non-primitive) would stringify to something like
 * "[object Object]" and corrupt on save. Arrays of primitives are fine (the
 * text cell just won't format them specially, unlike a `'list'` column).
 */
export function isPlainRenderableValue(value: unknown): boolean {
    if (value === null) return true;
    if (Array.isArray(value)) {
        return value.every(
            (v) =>
                v === null ||
                typeof v === 'string' ||
                typeof v === 'number' ||
                typeof v === 'boolean',
        );
    }
    return (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    );
}

/**
 * Build the column set to render from the loaded rows: `baseColumns` (the
 * doc's row-truth model, resolved from the schema endpoint) first, then any
 * *extra* payload keys found in the data — in first-seen order — appended as
 * plain text columns. Keys in {@link NON_COLUMN_KEYS} are skipped. When there
 * are no rows we fall back to `baseColumns` so an empty editor still shows the
 * model's columns.
 *
 * `earlierKeys` are keys only an earlier field-group version defined: they are
 * never promoted to editable extra columns, and those present in the rows come
 * back last as read-only columns (the stored value still round-trips on save).
 *
 * This is what makes the grid resilient to backend schema growth: a field added
 * server-side appears as an editable column rather than being dropped, and even
 * if it's an unrenderable shape it's still preserved on save (the payload bag is
 * the source of truth).
 */
export function deriveColumns(
    rows: { payload: Record<string, unknown> }[],
    baseColumns: ColumnDef[],
    earlierKeys: readonly string[] = [],
): ColumnDef[] {
    if (rows.length === 0) return baseColumns;

    const knownKeys = new Set(baseColumns.map((c) => c.key));
    const earlier = new Set(earlierKeys);
    const earlierPresent = new Set<string>();
    const extras: ColumnDef[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
        for (const [key, value] of Object.entries(row.payload)) {
            if (knownKeys.has(key) || NON_COLUMN_KEYS.has(key)) continue;
            if (earlier.has(key)) {
                earlierPresent.add(key);
                continue;
            }
            if (seen.has(key)) continue;
            if (!isPlainRenderableValue(value)) {
                // Not renderable as a flat text cell — skip promoting it to a
                // column. The value is still preserved untouched on save (the
                // payload bag is the source of truth); it's just not shown.
                console.warn(
                    `[GroundTruthHub] Skipping non-primitive extra column "${key}" — not renderable as text.`,
                );
                seen.add(key);
                continue;
            }
            seen.add(key);
            extras.push({ key, label: humanizeSnakeCase(key), kind: 'text' });
        }
    }

    const earlierColumns = earlierKeys
        .filter((key) => earlierPresent.has(key))
        .map((key): ColumnDef => ({
            key,
            label: humanizeSnakeCase(key),
            kind: 'text',
            readOnly: true,
        }));
    return extras.length === 0 && earlierColumns.length === 0
        ? baseColumns
        : [...baseColumns, ...extras, ...earlierColumns];
}

/** The LineItem columns shown before "Show all columns" is turned on. */
export const LINE_ITEM_DEFAULT_COLUMN_KEYS: ReadonlySet<string> = new Set([
    'item_no',
    'task_no',
    'description',
    'source_page',
]);

/** `columns` narrowed to `defaultKeys` (custom and read-only columns always stay) unless `showAll`; `null` defaultKeys means no narrowing. */
export function visibleColumns(
    columns: ColumnDef[],
    defaultKeys: ReadonlySet<string> | null,
    showAll: boolean,
): ColumnDef[] {
    if (showAll || defaultKeys === null) return columns;
    // Reviewer-defined and earlier-version columns are never tucked away.
    const narrowed = columns.filter(
        (c) => defaultKeys.has(c.key) || c.custom || c.readOnly,
    );
    return narrowed.length === 0 ? columns : narrowed;
}
